/**
 * The BotCircuits runtime's standard channel output -> the copilot panel's event protocol.
 *
 * The copilot app runs on the runtime like any other agent. It reports a turn two ways:
 *   - bot messages (subscribeBotMessage): the reply TEXT, quick-reply BUTTONS, and the
 *     Approve / Reject BUTTONS of a paused turn;
 *   - agent events (subscribeRuntimeDebugNotify `agentEvent`): tool_start / tool_end,
 *     todos_updated, approval_required.
 * The copilot's tools return console events of their own under `_events` (a resource
 * changed, navigate, show an upload card); they are forwarded when the tool finishes.
 *
 * Output: `{type, runId, seq, ts, data}` events for model/copilot-state.js, the same
 * protocol the Deep Agents copilot's API streams.
 *
 * The two subscriptions are separate queues, so a turn's last tool events can arrive after
 * its reply. Completion therefore waits `settleMs` after the reply, and events for a run
 * that has already completed are dropped, except durable resource/navigation events and
 * cards that can arrive after the reply on the other queue.
 *
 * Onboarding cards (website links, documents, use cases) are CUSTOM bot messages: shown as
 * `onboarding.card` with the message, and rebuilt from the stored transcript after a reload.
 * The "setting up your agent" card is `onboarding.setup` and does not end the run: the
 * analysis that ends with the use cases is still running in the same turn.
 *
 * The runtime ends every turn with a `turn_end` agent event. A turn that ended without a
 * reply (the model said nothing, or the turn crashed) would otherwise keep the panel waiting
 * forever, so after `turn_end` the run waits `replyGraceMs` for a late reply on the other
 * queue and then fails. A run with no activity at all for `idleMs` (a runtime that died
 * before it could send `turn_end`) fails too; `idleMs: 0` turns that off.
 *
 * A page that reloads mid-turn resumes it with `resumeRun`: the runtime stores the turn's
 * agent events (GET .../runtime/sessions/{id}/progress), they are replayed, and the live
 * events that arrived meanwhile follow, each once (by `eventId`). Replayed events carry
 * `replay: true`, so the page does not repeat their navigation.
 */

import { sanitizeClarification } from './clarification';
import { SETUP_CARD, sanitizeOnboardingCard } from './onboarding';
import { sanitizePublishCard } from './publishing';

export const APPROVE_INPUT = '__approve__';
export const REJECT_INPUT = '__reject__';
const SUB_AGENT = 'sub_agent';
const FORWARDED = new Set(['resource.changed', 'resource.focus', 'action', 'upload.requested', 'onboarding.setup', 'clarification.requested', 'testing.plan', 'testing.run']);
const MAX_SUGGESTIONS = 3;
// Live agent events that arrive before a reloaded page knows whether a turn is running.
const MAX_PENDING = 200;
export const NO_REPLY_ERROR = 'The copilot finished without replying. Please try again.';
export const CRASHED_ERROR = 'Something went wrong while working on that. Please try again.';
export const IDLE_ERROR = 'The copilot stopped responding. Please try again.';

function parse(value) {
    let current = value;
    for (let i = 0; i < 3 && typeof current === 'string'; i += 1) {
        try {
            current = JSON.parse(current);
        } catch (_) {
            return { text: current };
        }
    }
    return current && typeof current === 'object' ? current : {};
}

function isApprovalButtons(content) {
    return Array.isArray(content?.buttons)
        && content.buttons.some(b => b && (b.payload === APPROVE_INPUT || b.inputValue === APPROVE_INPUT));
}

function describeTool(name, args) {
    const ids = Object.entries(args || {})
        .filter(([k, v]) => (k.endsWith('id') || k === 'name') && (typeof v === 'string' || typeof v === 'number'))
        .map(([k, v]) => `${k}=${v}`).join(', ');
    const action = String(name || '').replace(/_/g, ' ');
    return ids ? `${action} (${ids})` : action;
}

export function createRuntimeTranslator(emit, {
    settleMs = 1500,
    replyGraceMs = 10000,
    idleMs = 10 * 60 * 1000,
    setTimer = (fn, ms) => setTimeout(fn, ms),
    clearTimer = handle => clearTimeout(handle),
    newRunId = () => `run-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
} = {}) {
    let run = null;
    let pending = [];
    let replaying = false;
    const seen = new Set();

    const send = (type, data = {}) => {
        if (!run) return;
        run.seq += 1;
        emit({ type, runId: run.id, seq: run.seq, ts: Date.now(), data, ...(replaying ? { replay: true } : {}) });
    };

    const fail = (owner, error) => {
        if (run !== owner || run.done) return;
        run.done = true;
        if (run.timer) clearTimer(run.timer);
        if (run.idle) clearTimer(run.idle);
        run.timer = null;
        run.idle = null;
        send('run.failed', { error });
    };

    // Any sign of life restarts the idle watchdog. A reply already settling the run wins.
    const touch = () => {
        if (!run || run.done || !idleMs) return;
        if (run.idle) clearTimer(run.idle);
        const owner = run;
        const activity = (run.activity += 1);
        run.idle = setTimer(() => {
            if (run === owner && !run.status && run.activity === activity) fail(owner, IDLE_ERROR);
        }, idleMs);
    };

    const onTurnEnd = payload => {
        // A reply already arrived (or the turn paused for input/approval): it ends the run.
        if (run.status) return;
        const owner = run;
        const error = payload.status === 'error' ? CRASHED_ERROR : NO_REPLY_ERROR;
        if (run.timer) clearTimer(run.timer);
        // The reply travels on the other queue and may still be on its way.
        run.timer = setTimer(() => {
            if (run === owner && !run.status) fail(owner, error);
        }, replyGraceMs);
    };

    const begin = runId => {
        if (run?.timer) clearTimer(run.timer);
        if (run?.idle) clearTimer(run.idle);
        run = { id: runId || newRunId(), seq: 0, open: [], texts: [], suggestions: [], approvals: [], status: null, done: false, timer: null, idle: null, activity: 0, counter: 0 };
        send('run.started', {});
        touch();
    };

    const ensureRun = () => {
        // A request for input can finish without a bot message. Keep a delayed final
        // reply on that same turn until startRun explicitly starts the answer turn.
        if (!run || (run.done && !run.awaitingInput)) begin();
    };

    const finish = () => {
        if (!run || run.done) return;
        run.done = true;
        run.timer = null;
        if (run.idle) clearTimer(run.idle);
        run.idle = null;
        const status = run.status || 'completed';
        send('run.completed', { status, text: run.texts.join('\n\n') });
        if (status === 'completed' && run.suggestions.length) send('suggestions', { items: run.suggestions });
    };

    const settle = status => {
        if (!run || run.done) return;
        // An approval pause wins over an ordinary reply.
        if (run.status !== 'awaiting_approval') run.status = status;
        if (run.timer) clearTimer(run.timer);
        run.timer = setTimer(finish, settleMs);
    };

    const inSubAgent = () => run.open.some(entry => entry.kind === SUB_AGENT);

    const onToolStart = payload => {
        run.counter += 1;
        const id = `t${run.counter}`;
        const agent = inSubAgent() ? 'subagent' : 'main';
        run.open.push({ id, name: payload.toolName, kind: payload.toolType, args: payload.input });
        if (payload.toolType === SUB_AGENT) {
            send('subagent.started', { id, subagent: payload.toolName, task: String(payload.input?.task || '').slice(0, 500) });
        } else {
            send('tool.started', { id, name: payload.toolName, agent });
        }
    };

    const onToolEnd = payload => {
        let index = -1;
        for (let i = run.open.length - 1; i >= 0; i -= 1) {
            if (run.open[i].name === payload.toolName) { index = i; break; }
        }
        const opened = index >= 0 ? run.open.splice(index, 1)[0] : null;
        const output = parse(payload.output);
        const failed = Boolean(payload.failed) || output.ok === false || Boolean(output.error);
        const error = failed ? String(output.error || 'The tool failed.').slice(0, 300) : null;
        const id = opened?.id || `t${(run.counter += 1)}`;
        if ((opened?.kind || payload.toolType) === SUB_AGENT) {
            send('subagent.finished', { id, subagent: payload.toolName, ok: !failed, summary: String(output.result || output.text || '').slice(0, 500) });
            return;
        }
        send('tool.finished', { id, name: payload.toolName, ok: !failed, error });
        if (failed) return;
        (Array.isArray(output._events) ? output._events : []).forEach(event => {
            if (event && FORWARDED.has(event.type)) {
                if (event.type === 'clarification.requested') {
                    if (!sanitizeClarification(event.data)) return;
                    run.awaitingInput = true;
                    send(event.type, event.data);
                    // This tool is itself a request for input; a final TEXT is optional.
                    settle('completed');
                } else send(event.type, event.data || {});
            }
        });
    };

    const firstDelivery = data => {
        const id = data?.agentEvent?.eventId;
        if (typeof id !== 'string' || !id) return true;
        if (seen.has(id)) return false;
        seen.add(id);
        return true;
    };

    const applyDebug = data => {
        const event = data?.agentEvent;
        if (!event || !run) return;
        const payload = event.payload || {};
        if (run.done) {
            if (event.type === 'tool_end' && payload.toolType !== SUB_AGENT) {
                const output = parse(payload.output);
                if (!payload.failed && output.ok !== false && !output.error) {
                    (Array.isArray(output._events) ? output._events : []).forEach(item => {
                        if (item?.type === 'clarification.requested' && sanitizeClarification(item.data)) {
                            run.awaitingInput = true;
                            send(item.type, item.data);
                        } else if (['testing.plan', 'testing.run', 'resource.changed', 'resource.focus', 'action'].includes(item?.type)) {
                            send(item.type, item.data);
                        }
                    });
                }
            }
            return;
        }
        touch();
        switch (event.type) {
            case 'tool_start':
                onToolStart(payload);
                break;
            case 'tool_end':
                onToolEnd(payload);
                break;
            case 'todos_updated':
                send('todos.updated', { todos: Array.isArray(payload.todos) ? payload.todos : [] });
                break;
            case 'approval_required':
                run.approvals = (payload.requests || []).filter(Boolean).map(r => ({
                    tool: r.tool, args: r.args, description: r.description || describeTool(r.tool, r.args),
                }));
                send('approval.required', { requests: run.approvals });
                settle('awaiting_approval');
                break;
            case 'turn_end':
                onTurnEnd(payload);
                break;
            default:
                break;
        }
    };

    return {
        /** A message was just sent from this page. */
        startRun: runId => {
            pending = [];
            begin(runId);
        },

        /**
         * A turn that was already running when this page loaded: replay its stored events
         * (`[{eventId, type, payload}]`), then the live ones that arrived meanwhile. Ignored
         * once this page has a run of its own.
         */
        resumeRun(runId, events = []) {
            if (run) return false;
            begin(runId);
            replaying = true;
            try {
                (Array.isArray(events) ? events : []).forEach(event => {
                    const data = { agentEvent: { type: event?.type, payload: event?.payload, eventId: event?.eventId } };
                    if (event?.type && firstDelivery(data)) applyDebug(data);
                });
            } finally {
                replaying = false;
            }
            const live = pending;
            pending = [];
            live.forEach(data => firstDelivery(data) && applyDebug(data));
            return true;
        },

        /** `{message}` from subscribeBotMessage. */
        onBotMessage(payload) {
            const message = payload?.message;
            if (!message || typeof message !== 'object') return;
            ensureRun();
            touch();
            const { type, content } = message;
            const card = type === 'CUSTOM' ? sanitizeOnboardingCard(content) : null;
            if (card?.kind === SETUP_CARD) {
                send('onboarding.setup', card);
                return;
            }
            const published = type === 'CUSTOM' ? sanitizePublishCard(content) : null;
            if (published) {
                // The publish outcome is the whole reply of its turn.
                if (published.text) {
                    run.texts.push(published.text);
                    send('message.delta', { text: (run.texts.length > 1 ? '\n\n' : '') + published.text });
                }
                send('publish.card', published);
                settle('completed');
                return;
            }
            if (card) {
                // A question card (or the use cases) is the whole reply, and ends the turn.
                if (card.text) {
                    run.texts.push(card.text);
                    send('message.delta', { text: (run.texts.length > 1 ? '\n\n' : '') + card.text });
                }
                send('onboarding.card', card);
                settle('completed');
                return;
            }
            if (type === 'BUTTONS') {
                if (isApprovalButtons(content)) {
                    if (!run.approvals.length) {
                        run.approvals = [{ tool: 'action', description: String(content.displayText || 'Approve this action?') }];
                        send('approval.required', { requests: run.approvals });
                    }
                    settle('awaiting_approval');
                    return;
                }
                run.suggestions = (content?.buttons || [])
                    .filter(b => b && b.title)
                    .map(b => ({ label: String(b.title).slice(0, 40), message: String(b.inputValue || b.payload || b.title).slice(0, 200) }))
                    .slice(0, MAX_SUGGESTIONS);
                if (content?.displayText) {
                    run.texts.push(content.displayText);
                    send('message.delta', { text: content.displayText });
                }
                settle('completed');
                return;
            }
            const text = typeof content === 'string' ? content : (content?.displayText || content?.text || '');
            if (text) {
                const separator = run.texts.length ? '\n\n' : '';
                run.texts.push(text);
                send('message.delta', { text: separator + text });
            }
            settle('completed');
        },

        /** `data` of subscribeRuntimeDebugNotify. */
        onDebugNotify(data) {
            if (!data?.agentEvent) return;
            if (!run) {
                // Kept for resumeRun: this page may not know yet that a turn is running.
                if (pending.length < MAX_PENDING) pending.push(data);
                return;
            }
            if (firstDelivery(data)) applyDebug(data);
        },

        dispose() {
            if (run?.timer) clearTimer(run.timer);
            if (run?.idle) clearTimer(run.idle);
            run = null;
            pending = [];
        },
    };
}

/** What to send for a message or an approval decision. */
export function inputFor({ message, approval } = {}) {
    if (message) return message;
    if (approval?.decision === 'approve') return APPROVE_INPUT;
    return approval?.message || REJECT_INPUT;
}

/** Stored transcript rows (GET /apps/{app}/sessions/{id}/messages) -> panel rows. */
export function transcriptRows(entries) {
    const rows = [];
    let status = 'completed';
    (Array.isArray(entries) ? entries : []).forEach(entry => {
        const message = entry?.message;
        if (entry?.sentBy === 'bot') {
            const content = message && typeof message === 'object' ? message.content : message;
            const card = message?.type === 'CUSTOM' ? sanitizeOnboardingCard(content) : null;
            const published = message?.type === 'CUSTOM' ? sanitizePublishCard(content) : null;
            if (published) {
                rows.push({ role: 'assistant', content: published.text, publishing: published });
                status = 'completed';
                return;
            }
            if (card?.kind === SETUP_CARD) {
                rows.push({ role: 'assistant', content: '', setup: card });
                status = 'completed';
                return;
            }
            if (card) {
                rows.push({ role: 'assistant', content: card.text, onboarding: card });
                status = 'completed';
                return;
            }
            if (message?.type === 'BUTTONS') {
                if (isApprovalButtons(content)) {
                    rows.push({ role: 'assistant', content: '', approvals: [{ tool: 'action', description: String(content.displayText || '') }] });
                    status = 'awaiting_approval';
                }
                return; // quick replies are not transcript text
            }
            const text = typeof content === 'string' ? content : (content?.displayText || content?.text || '');
            if (text) {
                rows.push({ role: 'assistant', content: text });
                status = 'completed';
            }
        } else if (entry) {
            const text = typeof message === 'string' ? message : (message?.content ?? '');
            if (text && text !== APPROVE_INPUT && text !== REJECT_INPUT) {
                rows.push({ role: 'user', content: String(text) });
            }
            if (text) status = 'completed';
        }
    });
    return { rows, status };
}
